import { useEffect, useRef, useState } from 'react';
import type { EmailOtpType } from '@supabase/supabase-js';
import { AuthShell } from '@/components/auth/AuthShell';
import { ScreenLoader } from '@/components/ScreenLoader';
import { ResetPasswordScreen } from './ResetPassword';
import { supabase } from '@/api/supabaseClient';

/**
 * /auth/confirm · prefetch-safe email-link landing
 * (plan: COORDINATION/plans/invite_link_prefetch_fix_2026-06-08.md).
 *
 * Invite / recovery / signup-confirm / email-change emails point HERE with a
 * `token_hash` instead of the auto-consuming GoTrue `/verify` redirect.
 * Verification runs in JS via `supabase.auth.verifyOtp({ token_hash, type })`,
 * so HTTP email scanners (mail.ru preview, antivirus "safe links") that don't
 * execute JS can't burn the one-time token before the human clicks — the bug that
 * locked out `kaligula.98@mail.ru` (8 JUN 2026).
 *
 * On success the Supabase session is established (onAuthStateChange → useAuth);
 * invite/recovery hand off to the set-password form (ResetPasswordScreen), the
 * rest (signup confirm / email change) drop straight into the app. On failure
 * (already-used / expired / malformed) we show the request-a-new-one card.
 */

type Phase = 'verifying' | 'set-password' | 'error';

/** Types whose flow needs the user to choose a password before entering the app. */
const SET_PASSWORD_TYPES: ReadonlySet<string> = new Set(['invite', 'recovery']);

function readParams(): { tokenHash: string; type: EmailOtpType | '' } {
  if (typeof window === 'undefined') return { tokenHash: '', type: '' };
  const q = new URLSearchParams(window.location.search);
  return {
    tokenHash: q.get('token_hash') ?? '',
    type: (q.get('type') as EmailOtpType | null) ?? '',
  };
}

export interface ConfirmLinkScreenProps {
  /** Terminal navigation into the app (AuthGate passes `goHome`). */
  onComplete: () => void;
  /** Test seam · skip the live verifyOtp call and start in this phase. */
  initialPhase?: Phase;
}

export function ConfirmLinkScreen({ onComplete, initialPhase }: ConfirmLinkScreenProps) {
  const [phase, setPhase] = useState<Phase>(initialPhase ?? 'verifying');
  const ran = useRef(false);

  useEffect(() => {
    if (initialPhase) return; // test seam · caller drives the phase directly
    if (ran.current) return; // verifyOtp is one-shot · never fire twice (StrictMode)
    ran.current = true;
    const { tokenHash, type } = readParams();
    if (!tokenHash || !type) {
      setPhase('error');
      return;
    }
    void supabase.auth
      .verifyOtp({ token_hash: tokenHash, type })
      .then(({ error }) => {
        if (error) {
          setPhase('error');
          return;
        }
        if (SET_PASSWORD_TYPES.has(type)) {
          setPhase('set-password');
        } else {
          // signup confirm / email change — session is live, no password step.
          onComplete();
        }
      })
      .catch(() => setPhase('error'));
  }, [initialPhase, onComplete]);

  if (phase === 'set-password') {
    // Session established by verifyOtp; hand off to the password form. `fromEmailLink`
    // satisfies ResetPasswordScreen's one-shot gate (the URL here carries token_hash,
    // not the recovery/invite hash markers that gate looks for).
    return (
      <ResetPasswordScreen
        token=""
        fromEmailLink
        onSuccess={onComplete}
        onTokenInvalid={onComplete}
      />
    );
  }

  if (phase === 'error') {
    return (
      <AuthShell
        title="Ссылка не валидна"
        subtitle="Срок действия истёк или ссылку уже открывали. Запроси новую через «Забыли пароль»."
      >
        <button type="button" className="btn btn--primary" onClick={onComplete}>
          На главную
        </button>
      </AuthShell>
    );
  }

  return <ScreenLoader label="Проверяем ссылку…" />;
}
